'use client';
import React, { useState } from 'react';
import { Bookmark, MessageCircle, Tag, Sparkles, Share2, Heart, Flag } from 'lucide-react';
import type { FeedPost, LookbookTag } from './FeedList';
import CommentsSection from './CommentsSection';
import Link from 'next/link';
import ReportModal from '@/components/ReportModal';

interface Props {
  post: FeedPost;
  isLiked: boolean;
  isSaved: boolean;
  onLike: () => void;
  onSave: () => void;
  onTagClick: (tag: LookbookTag) => void;
  isLoggedIn: boolean;
}

function formatDate(dateStr: string) {
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function getInitial(name: string | null) {
  return name ? name.charAt(0).toUpperCase() : '?';
}

export default function LookbookCard({ post, isLiked, isSaved, onLike, onSave, onTagClick, isLoggedIn }: Props) {
  const [hoveredTag, setHoveredTag] = useState<string | null>(null);
  const [commentOpen, setCommentOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);

  return (
    <article className="bg-white rounded-2xl border border-cream-300 overflow-hidden hover:shadow-md hover:border-cream-400 transition-all duration-200 group">
      {/* Header */}
      <div className="px-6 pt-6 pb-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link href={`/profile/${post.author?.username || post.author?.id}`}>
            {post.author?.avatar_url ? (
              <img src={post.author.avatar_url} alt={post.author.display_name || ''} className="w-10 h-10 rounded-full object-cover" />
            ) : (
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-gold-300 to-gold-500 flex items-center justify-center text-sm font-bold text-white shrink-0">
                {getInitial(post.author?.display_name)}
              </div>
            )}
          </Link>
          <div>
            <div className="flex items-center gap-1.5">
              <Link href={`/profile/${post.author?.username || post.author?.id}`} className="text-sm font-semibold text-charcoal-900 hover:text-gold-600 transition-colors">
                {post.author?.display_name || post.author?.username || 'Unknown'}
              </Link>
              {post.author?.role === 'icon' && (
                <span className="flex items-center gap-0.5 px-1.5 py-0.5 bg-gold-50 border border-gold-200 rounded-full">
                  <Sparkles size={9} className="text-gold-500" />
                  <span className="text-xs font-medium text-gold-600" style={{ fontSize: '10px' }}>Icon</span>
                </span>
              )}
            </div>
            <div className="flex items-center gap-1.5 text-xs text-charcoal-400 mt-0.5">
              <span>@{post.author?.username}</span>
              <span>·</span>
              <span>{formatDate(post.created_at)}</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button className="p-1.5 rounded-lg hover:bg-cream-200 text-charcoal-400">
            <Share2 size={15} />
          </button>
          {isLoggedIn && (
            <button onClick={() => setReportOpen(true)} className="p-1.5 rounded-lg hover:bg-red-50 text-charcoal-400 hover:text-red-500 transition-colors">
              <Flag size={15} />
            </button>
          )}
        </div>
      </div>

      {/* Outfit Label */}
      {post.outfit_label && (
        <div className="px-6 mb-3">
          <p className="font-editorial text-lg font-semibold text-charcoal-900">{post.outfit_label}</p>
          {post.lookbook_items && post.lookbook_items.length > 0 && (
            <p className="text-xs text-charcoal-400 mt-0.5 flex items-center gap-1">
              <Tag size={11} />
              {post.lookbook_items.length} items tagged — tap to shop
            </p>
          )}
        </div>
      )}

      {/* Lookbook Image with Tags */}
      {post.lookbook_image_url ? (
        <div className="relative mx-6 mb-4 rounded-xl overflow-hidden">
          <img src={post.lookbook_image_url} alt={post.outfit_label || 'Lookbook'} className="w-full object-cover max-h-[500px]" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent" />
          {post.lookbook_items?.map((tag) => (
            <button key={tag.id}
              onClick={() => onTagClick(tag)}
              onMouseEnter={() => setHoveredTag(tag.id)}
              onMouseLeave={() => setHoveredTag(null)}
              className="absolute transform -translate-x-1/2 -translate-y-1/2"
              style={{ left: `${tag.x_position}%`, top: `${tag.y_position}%` }}
              aria-label={`View ${tag.item_name} by ${tag.brand}`}>
              <div className="relative">
                <div className="w-6 h-6 rounded-full bg-white/90 border-2 border-charcoal-900 flex items-center justify-center shadow-md tag-pulse hover:scale-110 transition-transform duration-150">
                  <div className="w-2 h-2 rounded-full bg-charcoal-900" />
                </div>
                {hoveredTag === tag.id && (
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 fade-in z-10 pointer-events-none">
                    <div className="bg-charcoal-900 text-white rounded-lg px-3 py-2 whitespace-nowrap shadow-xl">
                      <p className="text-xs font-semibold">{tag.brand}</p>
                      <p className="text-xs text-charcoal-400">{tag.item_name}</p>
                      {tag.price && <p className="text-xs font-semibold text-gold-400 mt-0.5">{tag.price}</p>}
                      <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-charcoal-900" />
                    </div>
                  </div>
                )}
              </div>
            </button>
          ))}
        </div>
      ) : (
        <div className="mx-6 mb-4 rounded-xl bg-cream-200 aspect-[4/3] flex items-center justify-center">
          <p className="text-sm text-charcoal-400">No image</p>
        </div>
      )}

      {/* Tag Pills */}
      {post.lookbook_items && post.lookbook_items.length > 0 && (
        <div className="px-6 mb-4 flex gap-2 flex-wrap">
          {post.lookbook_items.map((tag) => (
            <button key={`pill-${tag.id}`} onClick={() => onTagClick(tag)}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-cream-100 border border-cream-300 rounded-full text-xs font-medium text-charcoal-700 hover:bg-cream-200 hover:border-cream-400 active:scale-95 transition-all duration-150">
              <Tag size={10} />
              {tag.brand} — {tag.item_name}
              {tag.price && <span className="text-gold-500 font-semibold">{tag.price}</span>}
            </button>
          ))}
        </div>
      )}

      {/* Footer */}
      <div className="px-6 py-3 border-t border-cream-200 bg-cream-50 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button onClick={onLike} disabled={!isLoggedIn}
            className={`flex items-center gap-1.5 text-sm font-medium transition-all duration-150 active:scale-95 ${isLiked ? 'text-red-500' : 'text-charcoal-400 hover:text-charcoal-700'} disabled:opacity-50`}>
            <Heart size={15} className={isLiked ? 'fill-red-500' : ''} />
            <span className="tabular-nums">{post.like_count}</span>
          </button>
          <button onClick={onSave} disabled={!isLoggedIn}
            className={`flex items-center gap-1.5 text-sm font-medium transition-all duration-150 active:scale-95 ${isSaved ? 'text-gold-500' : 'text-charcoal-400 hover:text-charcoal-700'} disabled:opacity-50`}>
            <Bookmark size={15} className={isSaved ? 'fill-gold-400' : ''} />
            <span className="tabular-nums">{post.save_count}</span>
          </button>
          <button onClick={() => setCommentOpen(!commentOpen)}
            className="flex items-center gap-1.5 text-sm font-medium text-charcoal-400 hover:text-charcoal-700 transition-colors">
            <MessageCircle size={15} />
            <span className="tabular-nums">{post.comment_count}</span>
          </button>
          {post.tag_click_count > 0 && (
            <div className="flex items-center gap-1.5 text-sm text-charcoal-400">
              <Tag size={13} />
              <span className="tabular-nums">{post.tag_click_count.toLocaleString()} clicks</span>
            </div>
          )}
        </div>
        <span className="text-xs text-charcoal-400 font-medium">Lookbook</span>
      </div>

      {commentOpen && (
        <div className="border-t border-cream-200">
          <CommentsSection postId={post.id} isLoggedIn={isLoggedIn} />
        </div>
      )}

      {reportOpen && (
        <ReportModal postId={post.id} onClose={() => setReportOpen(false)} />
      )}
    </article>
  );
}